import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap, MessageCircle } from "lucide-react";
import { NAV, CONTACT } from "../data/content";
import { useUI } from "../context/UIContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openUpload } = useUI();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm"
            : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <span className="h-9 w-9 rounded-full bg-blue-700 grid place-items-center shadow-lg shadow-blue-900/30">
              <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </span>
            <span className="text-navy-900 font-heading font-semibold text-lg tracking-tight leading-none">
              Energie<span className="text-blue-700">·</span>Tarifberater
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={`text-sm font-medium link-underline transition-colors ${
                  location.pathname === item.to
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-navy-900"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={openUpload}
              data-testid="nav-upload-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-blue-700 hover:bg-blue-600 text-white px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            >
              Rechnung prüfen
            </button>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-btn"
              className="hidden sm:grid place-items-center h-10 w-10 rounded-full border border-slate-300 text-emerald-700 hover:bg-emerald-50 transition-colors"
              aria-label="WhatsApp"
            >
              <MessageCircle className="h-4.5 w-4.5" />
            </a>
            <button
              className="xl:hidden grid place-items-center h-10 w-10 rounded-full border border-slate-300 text-navy-900"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Menü öffnen"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-blue-950 xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 h-20 flex items-center justify-between border-b border-white/10">
              <span className="text-white font-heading font-semibold text-lg">Menü</span>
              <button
                onClick={() => setOpen(false)}
                className="grid place-items-center h-10 w-10 rounded-full border border-white/20 text-white"
                data-testid="mobile-menu-close"
                aria-label="Menü schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="px-6 py-6 flex flex-col">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    className="block py-4 text-2xl font-heading text-white border-b border-white/5"
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <button
                onClick={() => { setOpen(false); openUpload(); }}
                className="mt-8 rounded-full bg-blue-600 text-white px-8 py-4 font-medium"
                data-testid="mobile-upload-btn"
              >
                Rechnung kostenlos prüfen
              </button>
              <div className="mt-6 flex items-center gap-5 text-sm text-slate-400">
                <Link to="/impressum" className="hover:text-blue-300" data-testid="mobile-impressum-link">Impressum</Link>
                <Link to="/datenschutz" className="hover:text-blue-300" data-testid="mobile-datenschutz-link">Datenschutz</Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
